import { useEffect, useState } from "react";
import { type SessionInfo, roleLabel } from "../api";
import { hrefOf } from "./router";
import { toggleDark, toggleSidebar, useTheme } from "./theme";
import { brandLogo } from "../brand";
import { setBooks, useBooks } from "../books";
import { OutgoingTransfers, usePendingTransfers } from "../OutgoingTransfers";
import { can, Permission } from "@ohc/shared";

/**
 * The Ynex header: sidebar toggle on the left, then dark mode, full screen and the profile
 * menu on the right. Markup and classes are the template's. The template's cart, messages,
 * notifications, language and apps menus are left out, because none of them are things OHC
 * has; an icon that opens a menu of sample data is worse than no icon.
 */
export function Header({
  session,
  onSignOut,
}: {
  readonly session: SessionInfo;
  readonly onSignOut: () => void;
}): React.ReactElement {
  const theme = useTheme();
  const books = useBooks();
  // Transfers out still to explain, for everyone who sells or buys (Daren, 14 October 2026).
  const waiting = usePendingTransfers(can(session.user.role, Permission.SaleCreate));
  const [explaining, setExplaining] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    const onChange = (): void => setFullScreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  function toggleFullscreen(): void {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      // Not every browser allows it, iPad Safari included. Failing quietly is right: the
      // button is a convenience, not a feature anyone depends on.
      void document.documentElement.requestFullscreen?.().catch(() => undefined);
    }
  }

  const initials = session.user.fullName
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="app-header">
      <nav className="main-header !h-[3.75rem]" aria-label="Global">
        <div className="main-header-container ps-[0.725rem] pe-[1rem] ">
          <div className="header-content-left">
            <div className="header-element">
              <div className="horizontal-logo">
                <a href={hrefOf("home")} className="header-logo">
                  <img src={brandLogo("lockup-light")} alt="Oh-Ho Collectibles" className="desktop-logo" />
                  <img src={brandLogo("emblem")} alt="Oh-Ho Collectibles" className="toggle-logo" />
                  <img src={brandLogo("lockup-dark")} alt="Oh-Ho Collectibles" className="desktop-dark" />
                  <img src={brandLogo("emblem")} alt="Oh-Ho Collectibles" className="toggle-dark" />
                  <img src={brandLogo("lockup-dark")} alt="Oh-Ho Collectibles" className="desktop-white" />
                  <img src={brandLogo("emblem")} alt="Oh-Ho Collectibles" className="toggle-white" />
                </a>
              </div>
            </div>
            <div className="header-element md:px-[0.325rem] !items-center" onClick={() => toggleSidebar()}>
              <a
                aria-label="Hide Sidebar"
                className="sidemenu-toggle animated-arrow  hor-toggle horizontal-navtoggle inline-flex items-center"
                href="#!"
                onClick={(event) => event.preventDefault()}
              >
                <span />
              </a>
            </div>
            {/* Company books or personal books, never both together (D, 12 October 2026). */}
            <div className="header-element !items-center ps-2">
              <div className="inline-flex rounded-md border border-defaultborder overflow-hidden text-[0.75rem] font-semibold" role="group" aria-label="Which books are shown">
                <button
                  type="button"
                  aria-pressed={books === "company"}
                  className={`px-3 py-1.5 ${books === "company" ? "bg-primary text-white" : "bg-transparent text-defaulttextcolor"}`}
                  onClick={() => setBooks("company")}
                >
                  Company
                </button>
                <button
                  type="button"
                  aria-pressed={books === "personal"}
                  className={`px-3 py-1.5 ${books === "personal" ? "bg-[#1f9d6b] text-white" : "bg-transparent text-defaulttextcolor"}`}
                  onClick={() => setBooks("personal")}
                >
                  Personal
                </button>
              </div>
              {books === "personal" ? <span className="ms-3 text-[0.75rem] font-semibold text-[#14694a] hidden md:inline">Personal books: not the company's</span> : null}
            </div>
          </div>

          <div className="header-content-right">
            {waiting.transfers.length > 0 ? (
              <div className="header-element !items-center px-2">
                <button
                  type="button"
                  className="ti-btn ti-btn-danger-full !py-1 !px-3 !text-[0.75rem] !mb-0 whitespace-nowrap"
                  title="Transfers out from the OCBC alerts that nobody has explained yet"
                  onClick={() => setExplaining(true)}
                >
                  <i className="ri-error-warning-line" /> {waiting.transfers.length} {waiting.transfers.length === 1 ? "transfer" : "transfers"} out to explain
                </button>
              </div>
            ) : null}
            {explaining ? (
              <OutgoingTransfers
                transfers={waiting.transfers}
                onClose={() => {
                  setExplaining(false);
                  waiting.reload();
                }}
                onChanged={() => undefined}
              />
            ) : null}
            {session.device ? (
              <div className="header-element !items-center py-[1rem] md:px-[0.65rem] px-2 hidden sm:flex">
                <span
                  className="badge bg-primary/10 text-primary !text-[0.75rem] !py-[0.35rem] !px-[0.6rem]"
                  title="Device code used in every sale and buyback reference from this device"
                >
                  <i className="ti ti-device-ipad me-1 align-middle" />
                  Device {session.device.code}
                </span>
              </div>
            ) : null}

            {/* The page's figures fetched again, for one changed in another tab (D, 18 October 2026). */}
            <div className="header-element !items-center !py-[1rem] md:!px-[0.65rem] px-2">
              <button
                type="button"
                aria-label="Refresh this page"
                title="Refresh this page"
                className="flex flex-shrink-0 justify-center items-center rounded-full text-[1.25rem] text-[#536485] dark:text-white/50 hover:text-primary"
                onClick={() => window.dispatchEvent(new Event("ohc:refresh"))}
              >
                <i className="ri-refresh-line" />
              </button>
            </div>
            <div
              className="header-element header-theme-mode !items-center !py-[1rem] md:!px-[0.65rem] px-2"
              onClick={() => toggleDark()}
            >
              <a
                aria-label={theme.mode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="flex group flex-shrink-0 justify-center items-center gap-2 rounded-full font-medium transition-all text-xs dark:bg-bgdark dark:hover:bg-black/20 dark:text-[#8c9097] dark:text-white/50 dark:hover:text-white"
                href="#!"
                onClick={(event) => event.preventDefault()}
              >
                <i className={`bx ${theme.mode === "dark" ? "bx-sun" : "bx-moon"} header-link-icon`} />
              </a>
            </div>

            <div className="header-element header-fullscreen py-[1rem] md:px-[0.65rem] px-2">
              <a
                aria-label="Full screen"
                onClick={(event) => {
                  event.preventDefault();
                  toggleFullscreen();
                }}
                href="#!"
                className="inline-flex flex-shrink-0 justify-center items-center gap-2 !rounded-full font-medium dark:hover:bg-black/20 dark:text-[#8c9097] dark:text-white/50 dark:hover:text-white"
              >
                <i className={`bx ${fullScreen ? "bx-exit-fullscreen" : "bx-fullscreen"} header-link-icon`} />
              </a>
            </div>

            <div className="header-element md:!px-[0.65rem] px-2 hs-dropdown !items-center ti-dropdown [--placement:bottom-left]">
              <button
                id="dropdown-profile"
                type="button"
                className="hs-dropdown-toggle ti-dropdown-toggle !gap-2 !p-0 flex-shrink-0 sm:me-2 me-0 !rounded-full !shadow-none text-xs align-middle !border-0 !shadow-transparent "
              >
                <span className="inline-flex items-center justify-center !w-[2rem] !h-[2rem] rounded-full bg-primary text-white font-semibold text-[0.75rem]">
                  {initials}
                </span>
              </button>
              <div className="md:block hidden dropdown-profile">
                <p className="font-semibold mb-0 leading-none text-[#536485] text-[0.813rem] ">
                  {session.user.fullName}
                </p>
                <span className="opacity-[0.7] font-normal text-[#536485] block text-[0.6875rem] ">
                  {roleLabel(session.user.role)}
                </span>
              </div>
              <div
                className="hs-dropdown-menu ti-dropdown-menu !-mt-3 border-0 w-[11rem] !p-0 border-defaultborder hidden main-header-dropdown  pt-0 overflow-hidden header-profile-dropdown dropdown-menu-end"
                aria-labelledby="dropdown-profile"
              >
                <ul className="text-defaulttextcolor font-medium dark:text-[#8c9097] dark:text-white/50">
                  <li>
                    <a
                      className="w-full ti-dropdown-item !text-[0.8125rem] !gap-x-0  !p-[0.65rem] !inline-flex"
                      href={hrefOf("password")}
                    >
                      <i className="ti ti-lock text-[1.125rem] me-2 opacity-[0.7]" />
                      Sign-in settings
                    </a>
                  </li>
                  <li>
                    <a
                      className="w-full ti-dropdown-item !text-[0.8125rem] !p-[0.65rem] !gap-x-0 !inline-flex"
                      href="#!"
                      onClick={(event) => {
                        event.preventDefault();
                        onSignOut();
                      }}
                    >
                      <i className="ti ti-logout text-[1.125rem] me-2 opacity-[0.7]" />
                      Sign out
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}
